import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,  Result, SectionTitle, Separator,CorrectionBox,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider} from '../../../../components/course/CourseComponents';

const Chapitre2Content = () => {
  return (
    <>
    <InfoBox title="Objectif du chapitre" color="indigo">
      <p className="mb-0">
        Déterminer les coordonnées de la case cliquée par le joueur.
      </p>
    </InfoBox>      

    <Separator type="text" text="Détection de la case cliquée" color="gray-darker" size="md" />
      <SectionTitle>🖱️ 1/ Détection de la case cliquée</SectionTitle>

      <p>Maintenant, on aimerait savoir sur quelle case le joueur clique.</p>

      <p>Pour connaître le numéro de la colonne, il suffit de récupérer la position de la souris lors du clic, de la diviser par <InlineCode>largeur_case</InlineCode>, puis de garder la partie entière.</p>

      <p>Pour garder la partie entière, on utilise la fonction p5.js :</p>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>floor(x)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Renvoie l'entier inférieur. Exemple : floor(3.6) = 3</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>Pour connaître la position de la souris, on utilise les variables p5.js :</p>
      
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
          <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>mouseX</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">position x de la souris</td>
          </tr>
          <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>mouseY</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">position y de la souris</td>
          </tr>

          </tbody>
        </table>
      </TableContainer>
      <p>La variable <InlineCode>case_x</InlineCode> contiendra le numéro de colonne cliquée : 0, 1 ou 2.</p>

      <Note type="warning" title="⚠️ Important">
        En programmation, les numéros de ligne et de colonne dans les tableaux commencent à 0, pas à 1 !
      </Note>

      <CodeBlock language="javascript">
{`case_x = floor(mouseX / largeur_case);`}
      </CodeBlock>

      <p>Quelques exemples :</p>
      <ul className="list-disc list-inside mb-4">
        <li>Si le joueur clique entre 0 et 200, par exemple à 158 → floor(mouseX / largeur_case) = floor(158/200) = floor(0.075) = 0 → colonne 0</li>
        <li>Si le joueur clique entre 200 et 400, par exemple à 300 → floor(300 / 200) = floor(1.5) = 1 → colonne 1</li>
        <li>Si le joueur clique entre 400 et 600, par exemple à 550 → floor(550 / 200) = floor(2.75) = 2 → colonne 2</li>
      </ul>

      <p>On fait pareil pour <InlineCode>case_y</InlineCode>, pour avoir le numéro de la ligne :</p>

      <CodeBlock language="javascript">
{`case_y = floor(mouseY / hauteur_case);`}
      </CodeBlock>

      <Note type="exercice" title="🧪 Affiche les coordonnées de la case cliquée :">
        - Utilise <InlineCode>largeur_case</InlineCode> et <InlineCode>mouseX</InlineCode> pour déterminer <InlineCode>case_x</InlineCode><br/>
        - Utilise <InlineCode>hauteur_case</InlineCode> et <InlineCode>mouseY</InlineCode> pour déterminer <InlineCode>case_y</InlineCode><br/>
        - Affiche <InlineCode>case_x</InlineCode> et <InlineCode>case_y</InlineCode>, qui correspondent aux coordonnées de la case<br/><br/>
        
        <Result>Case cliquée - Ligne : 0 - Colonne : 0</Result><br/>
        
        
        <CorrectionBox>
          <CodeBlock language="javascript">
{`function mousePressed() {
  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);
  console.log("Case cliquée - Ligne : " + case_x + " - Colonne : " + case_y);
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>

      <Note type="warning" title="⚠️ Important">
        Si le joueur clique en dehors du plateau, on ne fait rien.
        En effet, mouseX et mouseY sont les coordonnées de la souris, et ne sont pas forcément dans le plateau. Elle peuvent être négatives ou supérieures à la largeur ou à la hauteur du canvas.
        Il faut donc vérifier que case_x et case_y sont entre 0 et 2.
      </Note>

      <Note type="exercice" title="🧪 Verifier que le clic est dans le plateau">
        Ajoute une condition pour vérifier que le clic est dans le plateau :<br/>
        &nbsp;&nbsp;- Si <InlineCode>case_x</InlineCode> ou <InlineCode>case_y</InlineCode> est inférieur à 0 ou supérieur à 2, on quitte la fonction.<br/>
        <br/>
        <Result>Clic en dehors du plateau et verifie qu'on ne fait rien.</Result>
        <CorrectionBox>
          <CodeBlock language="javascript" >
{`function mousePressed() {
  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);

  if (case_x < 0 || case_x > 2 || case_y < 0 || case_y > 2)  
    return; // Clique en dehors du plateau

  console.log("Case cliquée - Ligne : " + case_x + " - Colonne : " + case_y);
}`}
          </CodeBlock>
        </CorrectionBox>
      </Note>



      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ Code complet (Détection de la case cliquée)</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 2 : le Morpion

let largeur_case;
let hauteur_case;

function setup() {
  createCanvas(600, 600);
  largeur_case = width / 3;
  hauteur_case = height / 3;
}

function draw() {
  background(220, 220, 220);

  // Plateau de jeu
  strokeWeight(4);
  for (let i = 1; i < 3; i++) {
    line(i * largeur_case, 0, i * largeur_case, height);
    line(0, i * hauteur_case, width, i * hauteur_case);
  }
}

function mousePressed() {
  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);

  if (case_x < 0 || case_x > 2 || case_y < 0 || case_y > 2)  
    return; // Clique en dehors du plateau
  
  console.log("Case cliquée - Ligne : " + case_x + " - Colonne : " + case_y);
}`}
      </CodeBlock>

      <Note type="info" title="💡 Prêt pour la suite ?">
        Maintenant que tu as créé la grille du morpion et que tu peux détecter les clics, nous allons ajouter la logique du jeu !
      </Note>


   
  </>
  );
};

export default Chapitre2Content; 